import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CACHE = 'wclub_';

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('wclub_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Event emitter for cache updates - notifies components when data changes
const cacheListeners = new Map();

export const onCacheUpdate = (key, callback) => {
  if (!cacheListeners.has(key)) {
    cacheListeners.set(key, new Set());
  }
  cacheListeners.get(key).add(callback);
  return () => cacheListeners.get(key)?.delete(callback);
};

const notifyCacheUpdate = (key) => {
  const listeners = cacheListeners.get(key);
  if (listeners) {
    listeners.forEach(cb => { try { cb(); } catch (e) { console.warn('Cache listener error:', e); } });
  }
};

// localStorage helpers
const getCache = (key) => {
  try {
    const d = localStorage.getItem(CACHE + key);
    return d ? JSON.parse(d) : null;
  } catch { return null; }
};

const setCache = (key, data) => {
  try { 
    localStorage.setItem(CACHE + key, JSON.stringify(data)); 
  } catch (e) { 
    // Storage full - clear old caches and retry
    console.warn('Cache storage full, clearing old entries...');
    try {
      // Clear chat caches first (they're usually the biggest)
      localStorage.removeItem(CACHE + 'chat_general');
      localStorage.removeItem(CACHE + 'chat_private');
      localStorage.setItem(CACHE + key, JSON.stringify(data));
    } catch (e2) {
      // Still full - clear all wclub caches except tokens
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k?.startsWith(CACHE) && !k.includes('token')) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
      console.warn('Cleared all caches due to quota');
    }
  }
};

// Cache-first GET: return cached data immediately, sync in background
const cachedGet = async (path, cacheKey, params) => {
  const cached = getCache(cacheKey);

  try {
    const res = await api.get(path, params ? { params } : undefined);
    setCache(cacheKey, res.data);
    return res;
  } catch (err) {
    if (cached) return { data: cached };
    throw err;
  }
};

// Write-through: update localStorage + API
const cachedPost = async (path, data, cacheKey, appendFn) => {
  const res = await api.post(path, data);
  if (cacheKey && appendFn) {
    const cached = getCache(cacheKey) || [];
    setCache(cacheKey, appendFn(cached, res.data));
  }
  return res;
};

// Members
export const getMembers = () => cachedGet('/members', 'members');
export const getMember = (id) => cachedGet(`/members/${id}`, `member_${id}`);
export const createMember = async (data) => {
  const res = await api.post('/members', data);
  const cached = getCache('members') || [];
  setCache('members', [...cached, res.data]);
  notifyCacheUpdate('members');
  return res;
};
export const updateMember = async (id, data) => {
  const res = await api.put(`/members/${id}`, data);
  const cached = getCache('members') || [];
  setCache('members', cached.map(m => m.id === id ? res.data : m));
  notifyCacheUpdate('members');
  return res;
};
export const deleteMember = async (id) => {
  const res = await api.delete(`/members/${id}`);
  const cached = getCache('members') || [];
  setCache('members', cached.filter(m => m.id !== id));
  notifyCacheUpdate('members');
  return res;
};

// Force refresh members from API (bypasses cache)
export const refreshMembers = async () => {
  const res = await api.get('/members');
  setCache('members', res.data);
  notifyCacheUpdate('members');
  return res;
};

// Events
export const getEvents = () => cachedGet('/events', 'events');
export const createEvent = async (data) => {
  const res = await api.post('/events', data);
  const cached = getCache('events') || [];
  setCache('events', [...cached, res.data]);
  return res;
};
export const updateEvent = async (id, data) => {
  const res = await api.put(`/events/${id}`, data);
  const cached = getCache('events') || [];
  setCache('events', cached.map(e => e.id === id ? res.data : e));
  return res;
};
export const deleteEvent = async (id) => {
  const res = await api.delete(`/events/${id}`);
  const cached = getCache('events') || [];
  setCache('events', cached.filter(e => e.id !== id));
  return res;
};
export const attendEvent = (data) => api.post('/events/attend', data);

// Payments
export const getPayments = (memberId) => cachedGet('/payments', `payments_${memberId || 'all'}`, memberId ? { member_id: memberId } : undefined);
export const createPayment = async (data) => {
  const res = await api.post('/payments', data);
  return res;
};
export const updatePaymentStatus = (id, data) => api.patch(`/payments/${id}`, data);

// Chat
export const getChatMessages = (room = 'general') => cachedGet('/chat/messages', `chat_${room}`, { room });
export const sendChatMessage = async (data) => {
  const res = await api.post('/chat/messages', data);
  return res;
};

// Sommelier AI (no cache - always live, longer timeout for image analysis)
export const askSommelier = (data) => api.post('/sommelier', data, { timeout: 60000 });

// Auth
export const adminLogin = async (email, password) => {
  const res = await api.post('/auth/login', { email, password });
  if (res.data.access_token) {
    localStorage.setItem('wclub_admin_token', res.data.access_token);
  }
  return res;
};

export const verifyToken = async () => {
  try {
    const res = await api.post('/auth/verify');
    return res.data;
  } catch {
    return { valid: false, is_admin: false };
  }
};

export const adminLogout = () => {
  localStorage.removeItem('wclub_admin_token');
};

export const isAdminLoggedIn = () => {
  return !!localStorage.getItem('wclub_admin_token');
};

export default api;
